import { Cell } from '@tanstack/react-table'
import { useContext, useMemo } from 'react'

import { valueGet } from '@/components/Properties/utils'
import { EditableCell } from '@/components/Table'
import { EditableCellProps, UseColumnsProps } from '@/components/Table/types'
import { calculateFieldValues, getTextWidth } from '@/components/Table/utils'
import { LastEditedCellContext } from '@/components/TableAndProperties/LastEditedCellContext'
import { useMemoObject } from '@/hooks/index'

/**
 * Wrapper that reads lastEditedCell from context and passes ref to EditableCell.
 * Keeps column definitions stable when lastEditedCell changes (avoids focus loss).
 */
function CellWithLastEditedRef(props: EditableCellProps) {
	const context = useContext(LastEditedCellContext)
	const isLastEditedCell =
		context &&
		context.lastEditedCell?.rowId === props.cell?.row?.index &&
		context.lastEditedCell?.fieldName === props.cell?.column?.id
	const ref = isLastEditedCell ? context?.lastEditedCellRef : undefined
	return <EditableCell {...props} ref={ref} />
}

/**
 * Custom hook to generate table columns for the main Table, the Table.tsx component.
 *
 * @template Item - The type of the items in the table.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Array} params.properties - The properties of the table columns.
 * @param {Array} params.tableData - The data for the table.
 * @param {React.RefObject} params.refWrapper - The ref for the table wrapper.
 * @param {Object} params.lastEditedCell - Optional; when LastEditedCellContext is provided, not used (avoids column recreation).
 * @param {React.RefObject} params.lastEditedCellRef - Optional; when LastEditedCellContext is provided, not used.
 *
 * @returns {Array} The generated columns for the table.
 */
export const useTableColumns = <Item extends object>({
	properties,
	tableData,
	refWrapper,
	lastEditedCell: _lastEditedCell,
	lastEditedCellRef: _lastEditedCellRef,
	node,
}: UseColumnsProps<Item>): Array<any> => {
	const propertiesWithAutoWidth = properties?.filter((prop) => prop.autoWidth)

	const propWidths = propertiesWithAutoWidth?.reduce(
		(prev, acc) => calculateFieldValues(acc, tableData, prev), // Use the function here
		{} as Record<string, string>,
	)

	const cachedPropWidths = useMemoObject(propWidths ?? {})

	const columns = useMemo(() => {
		if (!properties) {
			return []
		}

		return properties.map((property) => {
			const maxValue = cachedPropWidths[property.field]

			const autoWidth =
				maxValue &&
				getTextWidth(
					maxValue,
					refWrapper,
					property.field === 'objectCode' ? 450 : 180,
					property.field === 'objectCode',
				)

			return {
				id: property.field,
				header: property.label,
				accessorKey: property.field,
				isVisible: !property.hideInTable,
				size: property.autoWidth ? autoWidth : property.width,
				minSize: property.minWidth,
				cell: (cellProps: Cell<any, any>) => {
					const customCellProps = cellProps as unknown as EditableCellProps
					const { cell, table } = customCellProps
					const item = cell.row.original
					const value = valueGet(property, item)
					const handleChange = table?.options?.meta?.handleChange

					return (
						<CellWithLastEditedRef
							autoFocus={true}
							properties={properties}
							columnModelProps={property}
							value={value}
							handleChange={handleChange}
							cell={cell}
							node={node}
						/>
					)
				},
			}
		})
	}, [cachedPropWidths, properties, refWrapper])

	return columns
}
